/*
 * One description of the content libraries, shared by the React pages and
 * by the build-time sitemap and prerender scripts.
 *
 * It exists because the same title, description and canonical now have to be
 * produced twice: in the browser by useSeo, and at build time by
 * scripts/prerender.js for the crawlers that never run the bundle. Two copies
 * drift, and the drift is invisible — Google reads the rendered head, an AI
 * crawler reads the prerendered one, and nothing ever compares them.
 *
 * CommonJS on purpose. webpack pulls it into the bundle and plain `node`
 * requires it from scripts/; a module only one of them can load defeats the
 * point of having it.
 */

const SITE = 'https://app.learnxops.com';
const SITE_NAME = 'LearnXops';
const PAGE_SIZE = 24;

/* Every published design and case study has its diagram here (an SVG). */
const DIAGRAM_HOST = 'https://sysdesign.app.lxop.in';

const AUTHOR = { '@type': 'Person', name: 'Sandip Das', url: 'https://www.learnxops.com/author/sandip-das/' };
const PUBLISHER = { '@id': 'https://www.learnxops.com/#organization' };

/* Category chips on /system-designs. The key is the URL segment. */
const CATEGORY_META = {
  'fundamentals':        { label: 'Fundamentals',        emoji: '🧭' },
  'networking':          { label: 'Networking & APIs',   emoji: '🌐' },
  'data':                { label: 'Data & Storage',      emoji: '🗄️' },
  'caching':             { label: 'Caching',             emoji: '⚡' },
  'messaging':           { label: 'Messaging & Events',  emoji: '📨' },
  'distributed-systems': { label: 'Distributed Systems', emoji: '🕸️' },
  'architecture':        { label: 'Architecture',        emoji: '🏛️' },
  'ci-cd':               { label: 'CI/CD & Delivery',    emoji: '🚀' },
  'containers':          { label: 'Containers & K8s',    emoji: '☸️' },
  'infrastructure':      { label: 'Infrastructure',      emoji: '🧱' },
  'observability':       { label: 'Observability & SRE', emoji: '📊' },
  'ai-mlops':            { label: 'AI & MLOps',          emoji: '🤖' },
  'security':            { label: 'Security',            emoji: '🔐' },
  'case-study':          { label: 'Case Studies',        emoji: '🏗️' },
};

/* Category chips on /case-studies: the system design categories, minus the
 * one that is itself called "Case Studies" (those are design interview
 * problems, and belong to the system design library). */
const CASE_STUDY_CATEGORY_META = Object.fromEntries(
  Object.entries(CATEGORY_META).filter(([id]) => id !== 'case-study'),
);

/* Track chips on /career-roadmaps. Same contract, different vocabulary. */
const TRACK_META = {
  'engineering':     { label: 'Engineering',     emoji: '💻' },
  'platform':        { label: 'Platform & Ops',  emoji: '⚙️' },
  'ai':              { label: 'AI & ML',         emoji: '🤖' },
  'data':            { label: 'Data',            emoji: '🗄️' },
  'security':        { label: 'Security',        emoji: '🔐' },
  'customer-facing': { label: 'Customer-Facing', emoji: '🤝' },
};

const LIBRARIES = {
  'system-designs': {
    basePath: '/system-designs',
    diagramPath: '/system-designs',
    breadcrumb: 'System Designs',
    allLabel: 'Browse all system designs',
    categories: CATEGORY_META,
    hubTitle: 'System Design Library — Cloud, DevOps, MLOps & Platform Engineering',
    hubDescription: 'Worked system design walkthroughs for cloud, DevOps, MLOps and platform '
      + 'engineering interviews: requirements, trade-offs, real numbers and component choices.',
    pagedTitle: (page, pages) => `System Design Library — Page ${page} of ${pages}`,
    pagedDescription: (page) => `Page ${page} of the LearnXops system design library: worked walkthroughs `
      + 'for cloud, DevOps, MLOps, AIOps and platform engineering interviews.',
    categoryTitle: (label) => `${label} System Designs`,
    categoryDescription: (label) => `Worked ${label} system design walkthroughs: the requirements, `
      + 'the trade-offs, real numbers and the component choices behind each design.',
  },
  'case-studies': {
    basePath: '/case-studies',
    diagramPath: '/case-studies',
    breadcrumb: 'Case Studies',
    allLabel: 'Browse all case studies',
    categories: CASE_STUDY_CATEGORY_META,
    hubTitle: 'Case Studies — Real-World Cloud, DevOps, MLOps & AI Engineering',
    hubDescription: 'Real-world engineering case studies for cloud, DevOps, MLOps and AI: public '
      + 'postmortems and production scenarios, with the architecture, the numbers and the lessons.',
    pagedTitle: (page, pages) => `Case Studies — Page ${page} of ${pages}`,
    pagedDescription: (page) => `Page ${page} of the LearnXops case study library: real-world cloud, `
      + 'DevOps, MLOps and AI engineering postmortems and production scenarios.',
    categoryTitle: (label) => `${label} Case Studies`,
    categoryDescription: (label) => `Real-world ${label} case studies: what happened in production, `
      + 'the architecture behind it, the numbers and the lessons to take to your own team.',
  },
  'career-roadmaps': {
    basePath: '/career-roadmaps',
    breadcrumb: 'Career Roadmaps',
    allLabel: 'Browse all career roadmaps',
    categories: TRACK_META,
    hubTitle: 'Career Roadmaps — Cloud, DevOps, MLOps, AIOps & DevSecOps',
    hubDescription: 'Step-by-step career roadmaps for cloud, DevOps, MLOps, AIOps, SRE and '
      + 'DevSecOps roles: the skills to learn, in what order, and what each stage is for.',
    pagedTitle: (page, pages) => `Career Roadmaps — Page ${page} of ${pages}`,
    pagedDescription: (page) => `Page ${page} of the LearnXops career roadmap library for cloud, `
      + 'DevOps, MLOps, AIOps, SRE and DevSecOps roles.',
    categoryTitle: (label) => `${label} Career Roadmaps`,
    categoryDescription: (label) => `Step-by-step ${label} career roadmaps: the skills to learn, `
      + 'in what order, and what each stage of the path is for.',
  },
};

/** Trim to a clean sentence boundary near the SERP description limit. */
function clampDescription(text, max = 158) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const stop = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! '), cut.lastIndexOf('? '));
  if (stop > max * 0.6) return cut.slice(0, stop + 1).trim();
  return `${cut.slice(0, cut.lastIndexOf(' ')).trim()}…`;
}

/**
 * The one place a library URL is spelled.
 *
 * Page 1 is the bare path and an empty category is no segment at all, so a
 * single listing never has two addresses. Existing URLs are unchanged: the
 * category segment is additive, which matters because the hub and detail URLs
 * have already been shared.
 */
function libraryPath(kind, { category = '', page = 1 } = {}) {
  const lib = LIBRARIES[kind];
  const base = category ? `${lib.basePath}/category/${category}` : lib.basePath;
  return page > 1 ? `${base}/page/${page}` : base;
}

function isKnownCategory(kind, category) {
  return !!(category && LIBRARIES[kind] && LIBRARIES[kind].categories[category]);
}

function categoryLabel(kind, category) {
  const m = LIBRARIES[kind] && LIBRARIES[kind].categories[category];
  return m ? m.label : category;
}

/** Categories worth a URL of their own, largest first. */
function listedCategories(kind, counts, min = 3) {
  const lib = LIBRARIES[kind];
  return Object.keys(lib.categories)
    .filter((id) => (counts[id] || 0) >= min)
    .sort((a, b) => (counts[b] || 0) - (counts[a] || 0) || a.localeCompare(b));
}

/**
 * The visible <h1>.
 *
 * Every category view used to render the same "System Designs" heading, which
 * told a reader and a crawler nothing about which 24 of 187 designs they were
 * looking at. Shared with the prerender so the static and rendered headings
 * are the same string.
 */
function listingHeading(kind, category) {
  const lib = LIBRARIES[kind];
  if (category && isKnownCategory(kind, category)) {
    return lib.categoryTitle(categoryLabel(kind, category));
  }
  return lib.breadcrumb;
}

/** Head metadata for a hub, a numbered page, or a category view of either. */
function listingMeta(kind, { category = '', page = 1, pages = 1 } = {}) {
  const lib = LIBRARIES[kind];

  // An unrecognised category is a URL nobody linked and the API answers it
  // with an empty grid. Canonicalising it would add a soft 404 to the index;
  // noindex leaves the route working for whoever mistyped it.
  if (category && !isKnownCategory(kind, category)) {
    return {
      title: lib.hubTitle,
      description: clampDescription(lib.hubDescription, 160),
      canonical: null,
      noindex: true,
    };
  }

  const label = categoryLabel(kind, category);
  let title;
  let description;
  if (category) {
    title = page > 1 ? `${lib.categoryTitle(label)} — Page ${page} of ${pages}` : lib.categoryTitle(label);
    description = lib.categoryDescription(label);
  } else {
    title = page > 1 ? lib.pagedTitle(page, pages) : lib.hubTitle;
    description = page > 1 ? lib.pagedDescription(page) : lib.hubDescription;
  }
  return {
    title,
    // Clamped only as a backstop: these strings are written to fit, and
    // trimming an authored description mid-sentence is worse than a long one.
    description: clampDescription(description, 170),
    canonical: `${SITE}${libraryPath(kind, { category, page })}`,
    noindex: false,
  };
}

/**
 * CollectionPage + ItemList + BreadcrumbList for a listing.
 *
 * The hubs carried no structured data at all, so the one page that actually
 * knows the shape of the catalogue — 187 designs, 24 per page, in this order —
 * described none of it. The ItemList positions are absolute, not per-page, so
 * page 3 does not claim to start at item 1.
 */
function listingJsonLd(kind, { category = '', page = 1, pages = 1, items = [] } = {}) {
  const lib = LIBRARIES[kind];
  if (!items.length || (category && !isKnownCategory(kind, category))) return null;

  const url = `${SITE}${libraryPath(kind, { category, page })}`;
  const crumbs = [{ '@type': 'ListItem', position: 1, name: lib.breadcrumb, item: `${SITE}${lib.basePath}` }];
  if (category) {
    crumbs.push({
      '@type': 'ListItem',
      position: 2,
      name: categoryLabel(kind, category),
      item: `${SITE}${libraryPath(kind, { category })}`,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `${url}#page`,
        url,
        name: listingMeta(kind, { category, page, pages }).title,
        isPartOf: { '@id': `${SITE}/#website` },
        inLanguage: 'en',
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: items.length,
          itemListOrder: 'https://schema.org/ItemListOrderAscending',
          itemListElement: items.map((it, i) => ({
            '@type': 'ListItem',
            position: (page - 1) * PAGE_SIZE + i + 1,
            url: `${SITE}${lib.basePath}/${it.slug}`,
            name: it.title,
          })),
        },
      },
      { '@type': 'BreadcrumbList', itemListElement: crumbs },
    ],
  };
}

/**
 * Head metadata for one library page.
 *
 * Self-canonical: these libraries exist only on app.learnxops.com, so these
 * are the pages that should rank. TechArticle is the closest type — Article
 * would overstate it and HowTo was deprecated in 2023.
 */
/** A library item's diagram, or null when the library has none. */
function diagramUrl(lib, slug) {
  return lib && lib.diagramPath && slug ? `${DIAGRAM_HOST}${lib.diagramPath}/${encodeURIComponent(slug)}/diagram.svg` : null;
}

function detailMeta(kind, item) {
  const lib = LIBRARIES[kind];
  if (!item || !item.title || !item.slug) return { type: 'article' };

  const url = `${SITE}${lib.basePath}/${item.slug}`;
  const published = item.published_at || item.created_at || undefined;
  // The architecture diagram is the page's image (libraries that have one).
  const image = lib.diagramPath ? {
    '@type': 'ImageObject',
    url: diagramUrl(lib, item.slug),
    contentUrl: diagramUrl(lib, item.slug),
    encodingFormat: 'image/svg+xml',
    caption: `${item.title}: architecture diagram`,
  } : undefined;

  return {
    title: item.title,
    description: clampDescription(item.summary),
    canonical: url,
    type: 'article',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'TechArticle',
          '@id': `${url}#article`,
          headline: item.title,
          description: item.summary || undefined,
          url,
          mainEntityOfPage: url,
          datePublished: published,
          dateModified: item.updated_at || published,
          articleSection: item.category || undefined,
          image,
          // Case studies based on a public postmortem cite it.
          citation: Array.isArray(item.sources) && item.sources.length
            ? item.sources.map((x) => x.url) : undefined,
          keywords: Array.isArray(item.tags) && item.tags.length ? item.tags.join(', ') : undefined,
          timeRequired: item.read_minutes ? `PT${item.read_minutes}M` : undefined,
          inLanguage: 'en',
          author: AUTHOR,
          publisher: PUBLISHER,
          // The teaser is free and the rest is gated, so the accurate shape is
          // isAccessibleForFree:false scoped by hasPart. Declaring the whole
          // page gated forfeits Google's flexible sampling and is simply wrong.
          isAccessibleForFree: false,
          hasPart: {
            '@type': 'WebPageElement',
            isAccessibleForFree: false,
            cssSelector: '.lx-gated',
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: lib.breadcrumb, item: `${SITE}${lib.basePath}` },
            { '@type': 'ListItem', position: 2, name: item.title },
          ],
        },
      ],
    },
  };
}

module.exports = {
  SITE,
  SITE_NAME,
  PAGE_SIZE,
  CATEGORY_META,
  CASE_STUDY_CATEGORY_META,
  TRACK_META,
  LIBRARIES,
  clampDescription,
  libraryPath,
  isKnownCategory,
  categoryLabel,
  listedCategories,
  listingHeading,
  listingMeta,
  listingJsonLd,
  detailMeta,
  diagramUrl,
};
